// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// React
import React, { useEffect, useRef, useState } from "react";

// Scripts: Back up recordings locally
import {
  getStoredRecordings,
  storeRecording,
  clearRecording,
} from "../backupRecordings.js";

// Scripts: Tracking errors
import { ErrorState, trackError } from '../errorHandling.js';

// Scripts: Other
import { useLoadedData, LoadedDataState } from "../loadedData/loadedData.js";


// ----------------------------------------------------------------------------
//   Constants
// ----------------------------------------------------------------------------


// customize here for each video
const AUDIO_VOLUME_WEBCAM = 1.0; // 0..1
const AUDIO_VOLUME_VIDEO = 0.2; // 0..1
// end customization

let contentType = null;



// ----------------------------------------------------------------------------
//   Functions
// ----------------------------------------------------------------------------


function drawBufferingIcon( ctx, width, height ) {

  // Back up the context
  ctx.save();

  // Semi-transparent dark overlay
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.fillRect(0, 0, width, height);

  // Draw simple spinning circle indicator
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) * 0.1;
  const rotation = (Date.now() / 200) % (2 * Math.PI); // Rotate based on time

  // Styling
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';

  // Draw partial arc that appears to spin
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, rotation, rotation + (Math.PI * 2 / 3));
  ctx.stroke();

  // Restore the context
  ctx.restore();
  return ctx;
}


function drawTimestamp( ctx, startTime, width, height ) {

  // Compute timer text values
  const elapsed = Math.floor((Date.now() - startTime) / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;

  // Configure the timer text style
  const timerText = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  const fontSize = 12;
  const padding  = 6;

  // Measure the text, to configure the background
  ctx.font = `bold ${fontSize}px monospace`;
  const textMetrics = ctx.measureText(timerText);
  const textWidth   = textMetrics.width;
  const textHeight  = fontSize;

  // When flipped, position on LEFT (CSS will flip it to appear on right)
  // When not flipped, position on RIGHT
  const bgX = width  - textWidth  - (padding * 2) - 10;
  const bgY = height - textHeight - (padding * 2) - 10;

  // Save context state before applying transforms
  ctx.save();

  // Draw semi-transparent black background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
  ctx.fillRect(bgX, bgY, textWidth + (padding * 2), textHeight + (padding * 2));

  // Draw white timer text
  ctx.fillStyle = 'white';
  ctx.textBaseline = 'top';
  ctx.fillText(timerText, bgX + padding, bgY + padding);

  // Restore context state
  ctx.restore();
}


/**
 * Safely get a capture stream.
 */
function getCaptureStream( $element ) {

  // By default, the function is captureStream
  try {
    return $element.captureStream();
  }

  // The captureStream method doesn't exist on video elements in Safari,
  // and the Firefox-specific mozCaptureStream gives iffy results,
  // but we can forward the video content to a canvas and use that instead
  catch (e) {

    // Create canvas to draw combined video
    const videoCanvas = document.createElement('canvas');
    const videoCtx = videoCanvas.getContext('2d');

    const videoWidth  = $element.videoWidth;
    const videoHeight = $element.videoHeight;

    // Adjust canvas size to match webcam video dimensions
    videoCanvas.width  = videoWidth;
    videoCanvas.height = videoHeight;

    // Start animation loop
    function draw() {
      videoCtx.drawImage( $element, 0, 0, videoWidth, videoHeight);
      requestAnimationFrame(draw);
    }
    draw();

    // Create a capture stream from the canvas
    return videoCanvas.captureStream();
  }
}


/**
 * Create a MediaRecorder object that combines the two sources into one.
 */
export function createMediaRecorder( $webcam, $video, useWebcam = true, updateResult = null ) {

  // Track stream
  let videoStream;

  // If not using webcam, just return the video stream
  if (!useWebcam) {
    return new MediaRecorder( getCaptureStream($video) );
  }

  // Get the webcam stream from the canvas element
  const webcamStream = $webcam.srcObject;
  if (!webcamStream) {
    console.warn("screen stream is not ready. Try again");
    return;
  }


  // --------------------------------------------------------------------------
  //   Create objects to record combined media
  // --------------------------------------------------------------------------

  // Create combined audio context
  const audioCtx = new AudioContext();

  // Create canvas to draw combined video
  const videoCanvas = document.createElement('canvas');
  const videoCtx = videoCanvas.getContext('2d');

  // Debug styling to view canvas in real time
  // videoCanvas.style.position = "fixed"
  // videoCanvas.style.top  = "0px"
  // videoCanvas.style.left = "0px"
  // videoCanvas.style.zIndex = "99"
  // document.body.append( videoCanvas )


  // --------------------------------------------------------------------------
  //   Combine Audio
  // --------------------------------------------------------------------------

  // Audio Source 1: Webcam Recording
  const source1 = audioCtx.createMediaStreamSource(webcamStream);
  const gain1 = audioCtx.createGain();
  gain1.gain.value = AUDIO_VOLUME_WEBCAM;
  source1.connect(gain1);

  // Audio Source 2: Practice Video
  let source2;
  let gain2;
  try {
    videoStream = getCaptureStream($video);
    source2 = audioCtx.createMediaStreamSource(videoStream);
    gain2   = audioCtx.createGain();
    gain2.gain.value = AUDIO_VOLUME_VIDEO;
    source2.connect(gain2);
  }

  // Track with Sentry
  catch (e) {
    console.error("Couldn't get audio for practice video: ", e);
    trackError( ErrorState.NO_PRACTICE_AUDIO, e )
  }

  // Combined Audio
  const dest = audioCtx.createMediaStreamDestination();
  gain1.connect(dest);
  if (gain2) gain2.connect(dest);


  // --------------------------------------------------------------------------
  //   Combine video
  // --------------------------------------------------------------------------

  // Adjust canvas size to match webcam video dimensions
  videoCanvas.width  = $webcam.videoWidth;
  videoCanvas.height = $webcam.videoHeight;

  // Compute recording video size to fit in corner of combined video
  const videoAspectRatio = $video.videoWidth / $video.videoHeight;
  const videoWidth  = videoCanvas.width / 3;
  const videoHeight = videoWidth / videoAspectRatio;

  // Track when the video starts for timer
  const startTime = Date.now();

  // Start animation loop
  let animationFrameId;
  function draw() {

    // Draw webcam video across the whole canvas, and recording in top left corner
    videoCtx.drawImage( $webcam, 0, 0, videoCanvas.width, videoCanvas.height);
    videoCtx.drawImage( $video,  0, 0, videoWidth, videoHeight);

    // If the video is currently buffering, render a simple loading icon
    if ($video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      drawBufferingIcon( videoCtx, videoWidth, videoHeight );
    }

    // Add a timestamp in the bottom right corner, for validation
    drawTimestamp( videoCtx, startTime, videoCanvas.width, videoCanvas.height );

    // Loop
    animationFrameId = requestAnimationFrame(draw);
  }
  draw();


  // --------------------------------------------------------------------------
  //   Combine everything
  // --------------------------------------------------------------------------

  // Create output stream: audio + video from both sources
  const canvasStream = videoCanvas.captureStream();
  const outputStream = new MediaStream();
  outputStream.addTrack(canvasStream.getVideoTracks()[0]);
  outputStream.addTrack(dest.stream.getAudioTracks()[0]);

  // Return a MediaRecorder object recording the outut stream
  const mediaRecorder = new MediaRecorder(outputStream);

  // Store cleanup references
  mediaRecorder._cleanup = () => {
    cancelAnimationFrame(animationFrameId);
    if (audioCtx.state !== 'closed') {
      audioCtx.close();
    }
    canvasStream.getTracks().forEach( track => track.stop() );
    outputStream.getTracks().forEach( track => track.stop() );
    dest.stream.getTracks().forEach(  track => track.stop() );
    webcamStream.getTracks().forEach( track => track.stop() );
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
    }
  };

  // Initialize chunks
  let chunks = [];

  // When media recorder data available, append it to the list of chunks
  mediaRecorder.ondataavailable = event => {
    if (event.data.size > 0) {
      chunks.push(event.data);
    }
  }

  // When media recorder stops, upload the resulting video
  mediaRecorder.onstop = async () => {

    // Convert the recording to a blob
    const blob = new Blob(chunks, { contentType });

    // Generate a title for the recording
    const title = (new Date()).toLocaleString();

    // Back up the recording locally
    let recordingID
    try {
      recordingID = await storeRecording(title, blob);
    } catch (e) {
      console.error('Could not back up locally.');
    }

    // Resolve the promise with the stored ID
    if (updateResult) {
      updateResult.setSuccess({ recordingID, title, blob });
    }
  }

  // Return the new object
  return mediaRecorder;
}


/**
 * Record, save, and upload a video from the given MediaRecorder.
 */
export async function record(mediaRecorder) {
  return new Promise((resolve) => {

    // Initialize chunks
    let chunks = [];

    // When media recorder data available, append it to the list of chunks
    mediaRecorder.ondataavailable = event => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    }

    // When media recorder stops, upload the resulting video
    mediaRecorder.onstop = async () => {

      // Convert the recording to a blob
      const blob = new Blob(chunks, { contentType });

      // Generate a title for the recording
      const title = (new Date()).toLocaleString();

      // Back up the recording locally
      let recordingID
      try {
        recordingID = await storeRecording(title, blob);
      } catch (e) {
        console.error('Could not back up locally.');
      }

      // Resolve the promise with the stored ID
      resolve({ recordingID, title, blob })
    }

    // Start the media recorder
    mediaRecorder.start();
  })
}



// ----------------------------------------------------------------------------
//   Custom Media Recorder React Hook
// ----------------------------------------------------------------------------


/**
 * Custom hook tracking a combined media recorder object.
 */
export function useMediaRecorder({ videoRef, webcamRef, verbose = false }) {

  // Underlying media recorder object
  const [mediaRecorder, setMediaRecorder] = useState(null)

  // Store a reference to the current value of the object
  // Important - this lets us initialize & use it in separate callbacks
  const mediaRecorderRef = useRef();
  mediaRecorderRef.current = mediaRecorder;

  // Track recording state
  const [ isRecording, setIsRecording ] = useState(false);

  // Resulting video recording
  const [ result,      updateResult   ] = useLoadedData();

  // Return object
  return {

    // ------------------------------------------------------------------------
    //   State Variables
    // ------------------------------------------------------------------------

    /** Whether or not the referenced media is actively being recorded. */
    isRecording,

    /** The final recording object. */
    result,


    // ------------------------------------------------------------------------
    //   Functions
    // ------------------------------------------------------------------------

    /**
     * Start recording the referenced media source(s).
     *
     * Options:
     *   - `useWebcam`: If `false`, ignore the webcam when recording. Default `true`.
     *   - `playSourceVideo`: If `true`, invokes `play()` on the given video ref. Default `false`.
     */
    start: ( options = null ) => {
      if (verbose) console.debug('Media Recorder: Starting...');

      // Unpack the options, using appropriate fallbacks
      const { useWebcam = true, playSourceVideo = false } = (options ?? {});

      // If we already have a media recorder, use it;
      // otherwise, create a new recorder object from the two sources (webcam + video)
      let mediaRecorderCurrent;
      if (mediaRecorderRef.current) {
        mediaRecorderCurrent = mediaRecorderRef.current;
      }
      else {
        mediaRecorderCurrent = createMediaRecorder( webcamRef.current, videoRef.current, useWebcam, updateResult );
        setMediaRecorder( mediaRecorderCurrent );
      }

      // Start / resume the recorder, if possible
      if (mediaRecorderCurrent) {
        if (mediaRecorderCurrent.state === 'inactive') {
          mediaRecorderCurrent.start();
        }
        else if (mediaRecorderCurrent.state === 'paused') {
          mediaRecorderCurrent.resume();
        }
      }

      // Track the state
      updateResult.setWorking();
      setIsRecording(true);

      // Start the practice video, if desired
      if (playSourceVideo) {
        videoRef.current.play();
      }
    },

    /**
     * Pause the recording, but keep it open for further content.
     *
     * Options:
     *   - `pauseSourceVideo`: If `true`, invokes `pause()` on the given video ref. Default `false`.
     */
    pause: ( options = null ) => {
      if (verbose) console.debug('Media Recorder: Pausing...');

      // Unpack the options, using appropriate fallbacks
      const { pauseSourceVideo = false } = (options ?? {});

      // Prepare to track video errors
      let err = undefined;

      // Stop the video, holding onto any error that pops up
      // Ensures the media recorder can be stopped even if there's an error here
      if (pauseSourceVideo) {
        try {
          videoRef.current.pause();
        } catch (e) {
          err = e;
        }
      }

      // Pause the recording, if applicable
      mediaRecorderRef.current?.pause();

      // Track the state
      setIsRecording(false);

      // If an error was caught, it's safe to throw it now
      if (err !== undefined) throw err;
    },

    /**
     * Finish the recording, storing the resulting blob in the `result` state variable.
     *
     * Options:
     *   - `pauseSourceVideo`: If `true`, invokes `pause()` on the given video ref. Default `false`.
     */
    finish: ( options = null ) => {
      if (verbose) console.debug('Media Recorder: Finishing...');

      // Unpack the options, using appropriate fallbacks
      const { pauseSourceVideo = false } = (options ?? {});

      // Prepare to track video errors
      let err = undefined;

      // Stop the video, holding onto any error that pops up
      // Ensures the media recorder can be stopped even if there's an error here
      if (pauseSourceVideo) {
        try {
          videoRef.current.pause();
        } catch (e) {
          err = e;
        }
      }

      // Stop the recording and all track sources, if applicable
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current?.stop();
        mediaRecorderRef.current?._cleanup?.();
      }

      // Otherwise, force trigger the finish script
      else {
        updateResult.setSuccess(null)
      }

      // Track the state
      setIsRecording(false);

      // If an error was caught, it's safe to throw it now
      if (err !== undefined) throw err;
    },


    /**
     * Create a promise that waits for the recording to finish.
     * Useful to prevent race conditions when responding to the media recorder finishing.
     */
    waitForResult: async () => {

      if (mediaRecorderRef.current === null || mediaRecorderRef.current === undefined) {
        return new Promise((resolve) => resolve());
      }

      // Handles race conditions: checks immediately, then polls
      return new Promise((resolve) => {

        // If already not working, resolve immediately
        if (result.state !== LoadedDataState.WORKING) {
          resolve();
          return;
        }

        // Otherwise, poll until the state changes
        const interval = setInterval(() => {
          if (result.state !== LoadedDataState.WORKING) {
            clearInterval(interval);
            resolve();
          }
        }, 50); // Poll every 50ms
      });
    },

  };
}
